// Single source of truth for earnings-proximity classification on the frontend.
// Mirrors the backend NORMAL_CC_WINDOW_DAYS (45) calendar-risk logic in
// app/frameworks/cc_score (config.py + v7_0._score_2c_calendar_risk):
// earnings inside the normal covered-call horizon means a realistically-sold
// call would span the event, so it carries gap / IV-crush risk.

export const NORMAL_CC_WINDOW_DAYS = 45;

const RISK_COLOR = '#EF4444';     // red  — earnings ≤30d (a standard CC spans it)
const MONITOR_COLOR = '#F59E0B';  // amber — earnings 31–45d (only longer calls span it)

export type EarningsLevel = 'safe' | 'monitor' | 'risk' | 'unknown';
export type EarningsRating = 'good' | 'neutral' | 'bad' | 'very-bad';

export interface EarningsInfo {
  date: Date | null;
  days: number | null;   // calendar days until earnings (null when no/invalid date)
  isPast: boolean;
  level: EarningsLevel;
}

export interface EarningsBadge {
  show: boolean;
  color: string;
  label: string;
}

export function daysUntilEarnings(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
}

export function classifyEarnings(dateStr: string | null | undefined): EarningsInfo {
  const days = daysUntilEarnings(dateStr);
  if (days == null) return { date: null, days: null, isPast: false, level: 'unknown' };
  const date = new Date(dateStr as string);
  if (days < 0) return { date, days, isPast: true, level: 'unknown' };
  let level: EarningsLevel;
  if (days > NORMAL_CC_WINDOW_DAYS) level = 'safe';
  else if (days > 30) level = 'monitor';
  else level = 'risk';
  return { date, days, isPast: false, level };
}

// Map to the screener's 4-tier Rating palette used by ScoreBreakdown bars.
export function earningsRating(dateStr: string | null | undefined): EarningsRating {
  const { level, isPast } = classifyEarnings(dateStr);
  if (isPast) return 'neutral';
  switch (level) {
    case 'safe': return 'good';
    case 'monitor': return 'bad';      // amber
    case 'risk': return 'very-bad';    // red
    default: return 'neutral';         // unknown / no data
  }
}

// Compact badge descriptor for always-visible warnings (cards, position rows).
// Hidden when earnings is beyond the CC window, in the past, or unknown.
export function earningsBadge(dateStr: string | null | undefined): EarningsBadge {
  const info = classifyEarnings(dateStr);
  if (info.days == null || info.isPast || info.level === 'safe' || info.level === 'unknown') {
    return { show: false, color: '', label: '' };
  }
  const days = info.days;
  const when = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `${days}d`;
  return {
    show: true,
    color: info.level === 'risk' ? RISK_COLOR : MONITOR_COLOR,
    label: days <= 1 ? `Earnings ${when}` : `Earnings in ${when}`,
  };
}
